/**
 * W-032 — Grant subscription. Stories: US-805, US-A13.
 *
 * AC1 plan, duration, mandatory reason
 * AC2 the subscription activates and a transaction with source `admin` is written
 * AC3 EXTENDING COMPUTES FROM THE CURRENT EXPIRY, NOT FROM TODAY
 * AC5 the audit log records administrator, action, timestamp, reason
 *
 * The write itself is an edge function, not a table insert: the client has no
 * insert policy on `subscriptions` or `transactions` (docs/03 §4.3). This modal
 * computes and shows the resulting expiry so the operator sees, before
 * confirming, exactly what they are about to grant; the server recomputes it.
 */
import { Alert, DatePicker, Descriptions, Form, InputNumber, Modal, Select, Input } from 'antd'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useList, useNotification } from '@refinedev/core'
import { formatPrice } from '@horizon/i18n'
import { invokeEdgeFunction } from '@/lib/supabase'
import { useAdminLocale, useAdminTranslate } from '@/providers/i18n-provider'
import { computeNewExpiry } from '@/lib/subscription'
import type { Plan, Subscription } from '@/types'

export interface GrantSubscriptionModalProps {
  open: boolean
  userId: string
  userName: string
  /** The user's current active subscription, if any. */
  currentSubscription?: Subscription | null
  onClose: () => void
  onGranted: () => void
}

export const GrantSubscriptionModal = ({
  open,
  userId,
  userName,
  currentSubscription,
  onClose,
  onGranted,
}: GrantSubscriptionModalProps) => {
  const translate = useAdminTranslate()
  const locale = useAdminLocale()
  const { data: plansData, isLoading } = useList<Plan>({
    resource: 'plans',
    pagination: { mode: 'off' },
    sorters: [{ field: 'sort_order', order: 'asc' }],
  })
  const { open: notify } = useNotification()

  const [planId, setPlanId] = useState<string | undefined>(undefined)
  const [durationDays, setDurationDays] = useState<number>(30)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const plans = plansData?.data ?? []
  const selectedPlan = plans.find(plan => plan.id === planId)

  const currentExpiry =
    currentSubscription?.status === 'active' ? currentSubscription.expires_at : null

  const newExpiry = useMemo(
    () => computeNewExpiry(durationDays, currentExpiry),
    [durationDays, currentExpiry],
  )

  const reasonOk = reason.trim().length >= 5
  const canSubmit = Boolean(planId) && durationDays > 0 && reasonOk

  const handleOk = async () => {
    if (!planId) return
    setSubmitting(true)
    setError(null)
    try {
      // T-015: the deployed directory is `admin-subscription-grant`, not
      // `subscription-grant`. It insert/extends the subscription, writes a
      // transaction row with source='admin', and writes the audit entry (AC2,
      // AC5) in one transaction, keyed off `action` rather than a boolean.
      await invokeEdgeFunction<{ subscription_id: string; expires_at: string }>(
        'admin-subscription-grant',
        {
          user_id: userId,
          action: currentSubscription ? 'extend' : 'grant',
          plan_id: planId,
          days: durationDays,
          reason: reason.trim(),
        },
      )
      notify?.({
        type: 'success',
        message: translate('admin.screens.grant.grantedTitle'),
        description: translate('admin.screens.grant.grantedBody', {
          date: newExpiry.format('DD/MM/YYYY'),
        }),
      })
      setReason('')
      onGranted()
      onClose()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : translate('admin.screens.grant.failed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      title={translate('admin.screens.grant.title', { name: userName })}
      okText={translate('admin.screens.grant.confirm')}
      cancelText={translate('common.cancel')}
      okButtonProps={{ disabled: !canSubmit, loading: submitting }}
      onOk={() => void handleOk()}
      onCancel={onClose}
      destroyOnHidden
    >
      {currentExpiry ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={translate('admin.screens.grant.alreadyActiveTitle')}
          description={translate('admin.screens.grant.alreadyActiveBody', {
            date: dayjs(currentExpiry).format('DD/MM/YYYY'),
          })}
        />
      ) : null}

      <Form layout="vertical">
        <Form.Item label={translate('admin.common.plan')} required>
          <Select
            loading={isLoading}
            value={planId}
            onChange={value => {
              setPlanId(value)
              const plan = plans.find(candidate => candidate.id === value)
              if (plan) setDurationDays(plan.duration_days)
            }}
            options={plans.map(plan => ({
              value: plan.id,
              label:
                translate('admin.screens.grant.planOption', {
                  name: locale === 'ar' ? plan.name_ar : plan.name_fr,
                  price: formatPrice(plan.price_mru, locale),
                  days: plan.duration_days,
                }) + (plan.is_active ? '' : translate('admin.screens.grant.planInactive')),
            }))}
          />
        </Form.Item>

        <Form.Item
          label={translate('admin.screens.grant.durationLabel')}
          required
          help={translate('admin.screens.grant.durationHelp')}
        >
          <InputNumber
            min={1}
            max={3650}
            value={durationDays}
            onChange={value => setDurationDays(value ?? 0)}
          />
        </Form.Item>

        <Form.Item label={translate('admin.screens.grant.newExpiry')}>
          <DatePicker value={newExpiry} format="DD/MM/YYYY" disabled />
        </Form.Item>

        <Form.Item
          label={translate('admin.columns.reason')}
          required
          validateStatus={reason.length > 0 && !reasonOk ? 'error' : undefined}
          help={translate('admin.screens.grant.reasonHelp')}
        >
          <Input.TextArea
            rows={3}
            maxLength={500}
            value={reason}
            onChange={event => setReason(event.target.value)}
            placeholder={translate('admin.screens.grant.reasonPlaceholder')}
          />
        </Form.Item>
      </Form>

      {selectedPlan ? (
        <Descriptions size="small" column={1}>
          <Descriptions.Item label={translate('admin.screens.grant.scope')}>
            {selectedPlan.scope === 'all'
              ? translate('admin.screens.grant.scopeAll')
              : translate('admin.screens.grant.scopeSubjects')}
          </Descriptions.Item>
          <Descriptions.Item label={translate('admin.screens.grant.transactionSource')}>
            admin
          </Descriptions.Item>
        </Descriptions>
      ) : null}

      {error ? <Alert type="error" showIcon message={error} /> : null}
    </Modal>
  )
}
